import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Building2, DollarSign, TrendingUp, AlertTriangle, ExternalLink,
  CreditCard, CheckCircle, Loader2, FileText, RefreshCcw, Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { PartnerBadge } from '@/components/PartnerBadge';
import FeeRateCard from '@/components/FeeRateCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Partner Dashboard — replaces the generic /dashboard for users with
 * account_type === "partner". Renders MRR, subscription KPIs, and the
 * Stripe Connect Standard onboarding / status panel.
 *
 * Non-partner accounts are redirected to /dashboard so this page is
 * never a dead-end for the wrong role.
 */
export default function PartnerDashboard() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [stripe, setStripe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stripeLoading, setStripeLoading] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };

      const [meRes, stripeRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/partners/me`, { headers }),
        axios.get(`${API_URL}/api/partners/stripe/status`, { headers }),
      ]);

      if (meRes.status === 'fulfilled') {
        setProfile(meRes.value.data.profile);
        setStats(meRes.value.data.stats);
      } else {
        const status = meRes.reason?.response?.status;
        if (status === 403) {
          // Non-partner user — push them to the right dashboard
          navigate('/dashboard');
          return;
        }
        if (status === 401) {
          navigate('/');
          return;
        }
        toast.error('Failed to load partner profile');
      }

      if (stripeRes.status === 'fulfilled') {
        setStripe(stripeRes.value.data);
      }
    } catch (err) {
      console.error('Partner dashboard load failed:', err);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadAll();
    // Detect ?stripe_connected=true (Stripe redirect after onboarding)
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe_connected') === 'true') {
      toast.success('Stripe account connected!');
      // Strip the query so reloads are clean
      window.history.replaceState({}, '', '/partner-dashboard');
    } else if (params.get('stripe_refresh') === 'true') {
      toast.info('Stripe onboarding incomplete — please retry.');
      window.history.replaceState({}, '', '/partner-dashboard');
    }
  }, [loadAll]);

  const handleStripeOnboard = async () => {
    setStripeLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/partners/stripe/onboard`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.onboarding_url) {
        window.location.href = res.data.onboarding_url;
      } else {
        toast.error('Could not start Stripe onboarding');
        setStripeLoading(false);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Stripe onboarding failed');
      setStripeLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#00F0FF] animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center p-6">
        <Building2 className="w-16 h-16 text-neutral-700 mb-4" />
        <h1 className="text-2xl font-bold mb-2">No partner profile found</h1>
        <p className="text-neutral-400 mb-6 text-center max-w-md">
          This dashboard is only available to approved Enterprise Partners.
        </p>
        <Button onClick={() => navigate('/dashboard')} className="bg-[#00F0FF] text-black">
          Go to creator dashboard
        </Button>
      </div>
    );
  }

  const stripeStatus = stripe?.status || 'not_connected';
  const stripeConnected = stripeStatus === 'connected';

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="partner-dashboard">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-40">
        <div className="container mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="w-6 h-6 text-[#00F0FF]" />
            <h1 className="text-xl font-bold">Partner Console</h1>
            <PartnerBadge size="sm" />
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-neutral-400 hover:text-white"
            data-testid="partner-back-home"
          >
            Back to marketplace
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 md:px-12 py-10">
        {/* Company overview card */}
        <div className="bg-[#00F0FF]/5 border border-[#00F0FF]/30 rounded-2xl p-6 md:p-8 mb-10">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#00F0FF] font-semibold mb-2">
                Enterprise Partner
              </div>
              <h2 className="text-3xl md:text-4xl font-black mb-2" data-testid="partner-company-name">
                {profile.company_name}
              </h2>
              <p className="text-neutral-300 max-w-2xl">
                {profile.description || '—'}
              </p>
              <a
                href={profile.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[#00F0FF] hover:text-[#00C2CC] text-sm mt-3"
              >
                {profile.website} <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Approved</p>
              <p className="text-sm text-neutral-300">
                {profile.approved_at ? new Date(profile.approved_at).toLocaleDateString(
                  'en-US', { year: 'numeric', month: 'long', day: 'numeric' }
                ) : '—'}
              </p>
            </div>
          </div>
        </div>

        {/* Current fee rate banner — sourced from /api/me/fee */}
        <FeeRateCard compact={false} className="mb-10" />

        {/* KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <KpiCard
            icon={<DollarSign className="w-5 h-5" />}
            label="MTD Revenue"
            value={`$${(stats?.mtd_subscription_revenue || 0).toFixed(2)}`}
            accent="#00FF94"
            testId="kpi-mtd-revenue"
          />
          <KpiCard
            icon={<Users className="w-5 h-5" />}
            label="Active subs"
            value={stats?.active_subscriptions ?? 0}
            accent="#00F0FF"
            testId="kpi-active-subs"
          />
          <KpiCard
            icon={<AlertTriangle className="w-5 h-5" />}
            label="Past due"
            value={stats?.past_due_subscriptions ?? 0}
            accent="#00FF94"
            testId="kpi-past-due"
          />
          <KpiCard
            icon={<RefreshCcw className="w-5 h-5" />}
            label="Canceled"
            value={stats?.canceled_subscriptions ?? 0}
            accent="#888888"
            testId="kpi-canceled"
          />
        </div>

        {/* Stripe Connect Standard panel */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6 md:p-8 mb-10" data-testid="partner-stripe-panel">
          <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
            <div className="flex items-center gap-3">
              <CreditCard className="w-6 h-6 text-[#00F0FF]" />
              <h3 className="text-xl font-bold">Payments — Stripe Connect Standard</h3>
            </div>
            {stripeConnected && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#00FF94]/10 border border-[#00FF94]/30 text-[#00FF94] text-xs font-semibold" data-testid="stripe-connected-badge">
                <CheckCircle className="w-3.5 h-3.5" /> Connected
              </span>
            )}
          </div>

          {stripeStatus === 'not_connected' && (
            <>
              <p className="text-neutral-300 mb-4 max-w-2xl">
                Connect a Stripe Connect <strong>Standard</strong> account to accept subscriptions
                with your own branded Stripe Dashboard, full control of refunds & disputes,
                and higher volume limits than Express.
              </p>
              <Button
                onClick={handleStripeOnboard}
                disabled={stripeLoading}
                className="bg-[#635BFF] hover:bg-[#5449E6] text-white font-semibold px-6 py-5 rounded-xl"
                data-testid="stripe-onboard-btn"
              >
                {stripeLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting...</>
                ) : (
                  <>Connect Stripe (Standard) <ExternalLink className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </>
          )}

          {(stripeStatus === 'onboarding' || stripeStatus === 'in_progress') && (
            <>
              <p className="text-neutral-300 mb-4">
                Onboarding started but not complete. Finish verification on Stripe to start
                charging subscriptions.
              </p>
              <Button
                onClick={handleStripeOnboard}
                disabled={stripeLoading}
                className="bg-[#635BFF] hover:bg-[#5449E6] text-white font-semibold"
                data-testid="stripe-resume-btn"
              >
                {stripeLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting...</>
                ) : (
                  <>Resume Stripe onboarding <ExternalLink className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </>
          )}

          {stripeConnected && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                <StatusPill label="Charges" on={stripe?.charges_enabled} />
                <StatusPill label="Payouts" on={stripe?.payouts_enabled} />
                <StatusPill label="Details submitted" on={stripe?.details_submitted} />
              </div>
              <a
                href={stripe?.dashboard_url || 'https://dashboard.stripe.com/'}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#635BFF] hover:bg-[#5449E6] text-white font-semibold px-5 py-3 rounded-xl transition-colors"
                data-testid="stripe-open-dashboard-btn"
              >
                Open Stripe Dashboard <ExternalLink className="w-4 h-4" />
              </a>
            </>
          )}
        </div>

        {/* Compliance docs quick-links */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6 md:p-8">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-5 h-5 text-[#00F0FF]" />
            <h3 className="text-xl font-bold">Compliance & docs</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <DocLink href="/theailedger-security-posture.pdf" label="Security Posture (PDF)" />
            <DocLink href="/theailedger-mutual-nda.pdf" label="Mutual NDA template (PDF)" />
          </div>
        </div>
      </main>
    </div>
  );
}

function KpiCard({ icon, label, value, accent, testId }) {
  return (
    <div
      className="bg-neutral-900/50 border border-white/10 rounded-xl p-5 hover:border-white/20 transition-colors"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-2" style={{ color: accent }}>
        {icon}
        <span className="text-xs uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <div className="text-3xl font-black" style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}

function StatusPill({ label, on }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${on ? 'bg-[#00FF94]/5 border-[#00FF94]/30' : 'bg-neutral-800/50 border-white/10'}`}>
      {on ? (
        <CheckCircle className="w-4 h-4 text-[#00FF94]" />
      ) : (
        <TrendingUp className="w-4 h-4 text-neutral-500" />
      )}
      <span className={`text-sm ${on ? 'text-[#00FF94]' : 'text-neutral-400'}`}>{label}</span>
    </div>
  );
}

function DocLink({ href, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-between gap-2 bg-neutral-800/50 hover:bg-neutral-800 border border-white/10 rounded-lg px-4 py-3 transition-colors"
    >
      <span className="text-sm font-medium">{label}</span>
      <ExternalLink className="w-4 h-4 text-neutral-500" />
    </a>
  );
}
